// AI-Generated Code - 2025-01-27 - Change History Page

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  RefreshCw, 
  Search, 
  AlertTriangle, 
  Plus, 
  Minus, 
  Edit, 
  Globe,
  TrendingUp,
  TrendingDown,
  Shield
} from "lucide-react";

interface ScriptChange {
  scriptSrc: string;
  oldHash: string;
  newHash: string;
  changeType: 'added' | 'modified' | 'removed';
  changeDate: string;
  riskLevel: 'low' | 'medium' | 'high';
}

interface ChangeReport {
  scanId: number;
  domain: string;
  scanDate: string;
  scriptChanges: ScriptChange[];
  newThirdPartyDomains: string[];
  removedThirdPartyDomains: string[];
  securityScore: number;
  previousScore: number;
  alertsGenerated: number;
}

type CardFilter = "all" | "scans" | "scripts" | "domains" | "alerts";

export default function ChangeHistory() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDomain, setFilterDomain] = useState("all");
  const [filterChangeType, setFilterChangeType] = useState("all");
  const [activeCardFilter, setActiveCardFilter] = useState<CardFilter | null>(null);

  const { data: changeReports = [], isLoading, refetch } = useQuery<ChangeReport[]>({
    queryKey: ["/api/daily-scans/changes"],
    refetchInterval: 60000, // Refresh every minute
  });

  // Get unique domains for filter
  const domains = Array.from(new Set(changeReports.map(report => report.domain)));

  const handleCardClick = (filter: CardFilter) => {
    setActiveCardFilter(prev => prev === filter ? null : filter);
  };

  // Normalize domain for comparison (hostname only, no protocol)
  const normalizeDomain = (d: string) => {
    if (!d) return "";
    const s = d.trim().toLowerCase();
    try {
      return s.startsWith("http") ? new URL(s).hostname : s.split("/")[0].split(":")[0];
    } catch {
      return s;
    }
  };

  // Filter reports
  const filteredReports = changeReports.filter(report => {
    const reportDomainNorm = normalizeDomain(report.domain);
    const filterNorm = filterDomain === "all" ? "all" : normalizeDomain(filterDomain);
    const matchesDomain = filterNorm === "all" || reportDomainNorm === filterNorm || report.domain === filterDomain;
    const matchesSearch = 
      normalizeDomain(report.domain).includes(normalizeDomain(searchTerm)) ||
      report.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.scriptChanges.some(change => 
        change.scriptSrc.toLowerCase().includes(searchTerm.toLowerCase())
      );
    const hasScriptChanges = report.scriptChanges.length > 0;
    const hasDomainChanges = report.newThirdPartyDomains.length > 0 || report.removedThirdPartyDomains.length > 0;
    const hasAlerts = (report.alertsGenerated || 0) > 0;
    let matchesCard = true;
    if (activeCardFilter === "scans") matchesCard = true; // all reports are scans
    else if (activeCardFilter === "scripts") matchesCard = hasScriptChanges;
    else if (activeCardFilter === "domains") matchesCard = hasDomainChanges;
    else if (activeCardFilter === "alerts") matchesCard = hasAlerts;
    return matchesDomain && matchesSearch && matchesCard;
  });

  // Filter script changes by type
  const getFilteredScriptChanges = (changes: ScriptChange[]) => {
    if (filterChangeType === "all") return changes;
    return changes.filter(change => change.changeType === filterChangeType);
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'added': return <Plus className="h-4 w-4 text-green-600" />;
      case 'modified': return <Edit className="h-4 w-4 text-yellow-600" />;
      case 'removed': return <Minus className="h-4 w-4 text-red-600" />;
      default: return null;
    }
  };

  const getChangeTypeBadge = (type: string) => {
    switch (type) {
      case 'added':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">New</Badge>;
      case 'modified':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">Modified</Badge>;
      case 'removed':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">Removed</Badge>;
      default:
        return null;
    }
  };

  const getRiskLevelBadge = (risk: string) => {
    switch (risk) {
      case 'high':
        return <Badge variant="destructive">High Risk</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">Medium Risk</Badge>;
      case 'low':
        return <Badge variant="outline">Low Risk</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Change History</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalChanges = changeReports.reduce((sum, report) => sum + report.scriptChanges.length, 0);
  const totalDomainChanges = changeReports.reduce((sum, report) => 
    sum + report.newThirdPartyDomains.length + report.removedThirdPartyDomains.length, 0
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">DOM & Script Change History</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track all script and third-party domain changes detected by daily scans
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards - clickable to filter list */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          role="button"
          tabIndex={0}
          onClick={() => handleCardClick("scans")}
          onKeyDown={(e) => e.key === "Enter" && handleCardClick("scans")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 dark:hover:border-purple-600 ${
            activeCardFilter === "scans" ? "ring-2 ring-purple-500 shadow-md border-purple-300 dark:border-purple-600" : ""
          }`}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {changeReports.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Daily Scans</div>
          </CardContent>
        </Card>
        <Card
          role="button"
          tabIndex={0}
          onClick={() => handleCardClick("scripts")}
          onKeyDown={(e) => e.key === "Enter" && handleCardClick("scripts")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 dark:hover:border-blue-600 ${
            activeCardFilter === "scripts" ? "ring-2 ring-blue-500 shadow-md border-blue-300 dark:border-blue-600" : ""
          }`}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {totalChanges}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Script Changes</div>
          </CardContent>
        </Card>
        <Card
          role="button"
          tabIndex={0}
          onClick={() => handleCardClick("domains")}
          onKeyDown={(e) => e.key === "Enter" && handleCardClick("domains")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 dark:hover:border-orange-600 ${
            activeCardFilter === "domains" ? "ring-2 ring-orange-500 shadow-md border-orange-300 dark:border-orange-600" : ""
          }`}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {totalDomainChanges}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Domain Changes</div>
          </CardContent>
        </Card>
        <Card
          role="button"
          tabIndex={0}
          onClick={() => handleCardClick("alerts")}
          onKeyDown={(e) => e.key === "Enter" && handleCardClick("alerts")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 dark:hover:border-green-600 ${
            activeCardFilter === "alerts" ? "ring-2 ring-green-500 shadow-md border-green-300 dark:border-green-600" : ""
          }`}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {changeReports.reduce((sum, r) => sum + r.alertsGenerated, 0)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Alerts Generated</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by domain or script URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterDomain} onValueChange={setFilterDomain}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Domains</SelectItem>
            {domains.map(domain => (
              <SelectItem key={domain} value={domain}>{domain}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterChangeType} onValueChange={setFilterChangeType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Changes</SelectItem>
            <SelectItem value="added">New Scripts</SelectItem>
            <SelectItem value="modified">Modified Scripts</SelectItem>
            <SelectItem value="removed">Removed Scripts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Change Reports */}
      {filteredReports.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Changes Found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {changeReports.length === 0 
                ? "Complete at least two scans on the same URL (from the Dashboard) to see script and third-party domain changes here."
                : "No changes match your search criteria."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredReports.map((report) => {
            const filteredChanges = getFilteredScriptChanges(report.scriptChanges);
            const hasChanges = filteredChanges.length > 0 || 
                             report.newThirdPartyDomains.length > 0 || 
                             report.removedThirdPartyDomains.length > 0;

            return (
              <Card key={report.scanId} className="border-l-4 border-l-purple-500">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-purple-600" />
                        {report.domain}
                      </CardTitle>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Scanned: {new Date(report.scanDate).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Security Score</div>
                        <div className="flex items-center gap-1">
                          {report.securityScore > report.previousScore ? (
                            <TrendingUp className="h-4 w-4 text-green-600" />
                          ) : report.securityScore < report.previousScore ? (
                            <TrendingDown className="h-4 w-4 text-red-600" />
                          ) : (
                            <Shield className="h-4 w-4 text-gray-400" />
                          )}
                          <span className={`font-bold ${
                            report.securityScore >= 80 ? 'text-green-600' :
                            report.securityScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {report.securityScore}%
                          </span>
                          {report.previousScore > 0 && (
                            <span className="text-sm text-gray-500">
                              ({report.securityScore > report.previousScore ? '+' : ''}
                              {report.securityScore - report.previousScore}%)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Script Changes */}
                  {filteredChanges.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Script Changes ({filteredChanges.length})
                      </h3>
                      <div className="space-y-2">
                        {filteredChanges.map((change, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  {getChangeTypeIcon(change.changeType)}
                                  {getChangeTypeBadge(change.changeType)}
                                  {getRiskLevelBadge(change.riskLevel)}
                                </div>
                                <div className="text-sm font-mono text-gray-900 dark:text-white break-all">
                                  {change.scriptSrc}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  <div>Old Hash: {change.oldHash ? `${change.oldHash.substring(0, 16)}...` : 'N/A'}</div>
                                  <div>New Hash: {change.newHash ? `${change.newHash.substring(0, 16)}...` : 'N/A'}</div>
                                  <div>Detected: {new Date(change.changeDate).toLocaleString()}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Third-Party Domain Changes */}
                  {(report.newThirdPartyDomains.length > 0 || report.removedThirdPartyDomains.length > 0) && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Third-Party Domain Changes
                      </h3>
                      {report.newThirdPartyDomains.length > 0 && (
                        <div className="mb-2">
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 mb-2">
                            New Domains ({report.newThirdPartyDomains.length})
                          </Badge>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {report.newThirdPartyDomains.map((domain, idx) => (
                              <Badge key={idx} variant="outline" className="bg-green-50">
                                <Plus className="h-3 w-3 mr-1 text-green-600" />
                                {domain}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {report.removedThirdPartyDomains.length > 0 && (
                        <div>
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 mb-2">
                            Removed Domains ({report.removedThirdPartyDomains.length})
                          </Badge>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {report.removedThirdPartyDomains.map((domain, idx) => (
                              <Badge key={idx} variant="outline" className="bg-red-50">
                                <Minus className="h-3 w-3 mr-1 text-red-600" />
                                {domain}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No Changes Message - show when this report has no script/domain changes */}
                  {!hasChanges && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No script or third-party domain changes between the two compared scans for this domain.
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
